import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Avatar from 'flarum/common/components/Avatar';
import LessonOptions from '../utils/LessonOptions';
import { openMediaLightbox, uploadMedia, mediaTypeFor } from '../utils/LessonReviewMedia';
// Phase D2-A (v0.18.16): 课评媒体的前端体积上限**动态取**, 不写死。
//   改造前这里完全没有任何前端上限 (直传 COS 时 1GB 没问题); 改成走本地档后
//   文件要经本机 PHP 中转, 会撞上服务器封顶 (生产 upload_max_filesize=2M),
//   于是出现「前端放行、后端拒」的看不懂的错。
import { resolveUploadLimit, overLimitMessage } from '../utils/uploadLimits';

/**
 * ============================================================================
 * v0.18.0 — 「课评」modal (辉哥 10/18 拍板)
 *
 * ## 三种形态, 由**服务端**给的标志决定，不由前端推断
 *   · 教练 / 从未写   → 空白编辑态 (页脚三按钮: 取消 / 保存草稿 / 发布课评)
 *   · 教练 / 草稿     → 自动加载已存草稿续写 (规则 7), 顶部一条「草稿只有你能看到」
 *   · 教练 / 已发布   → **页脚两个按钮** (取消 / ✓ 更新课评), **没有**「保存草稿」(规则 8/19)
 *   · 学员 (readonly) → **无任何可编辑控件** (规则 20): 只有星级/文字/媒体的只读呈现,
 *                      媒体可点开大图/播放, **无删除 ×**
 *
 * ## ⛔ 星级默认 5 星 (辉哥 10/18 **追加规则**)
 *   `oninit` 里 rating 初值取 5, 不是 0/空。design.html 的 02 屏画的是「空星级」, 那是**过时稿**。
 *   连带后果 (已写进交付报告): 空态与已填写态的差别**只剩文字和媒体两处**。
 *
 * ## ⛔ 前端**不**做任何「能不能撤回」的保护
 *   「已发布态没有保存草稿按钮」是体验; 服务端 R1 会回 422 (见 SaveLessonReviewController)。
 *   这里不做乐观禁用以外的任何判断 —— 免得两边规则各写一份、各自漂移。
 * ============================================================================
 */
/**
 * ⛔⛔⛔ Flarum Modal 的**保留名**: className() / content() / title() / loading / hide() / loaded()
 *
 *   本组件第一版把「课评正文」这个状态字段命名成 `this.content`, 结果在 `oninit` 里
 *   一句 `this.content = ''` 就把**继承来的 `content()` 渲染方法**用字符串覆盖掉了 ——
 *   于是 core 的 `Modal.inner()` 里 `this.content()` 抛
 *   `TypeError: this.content is not a function`, **整个 modal 一个像素都渲染不出来**。
 *   同一批还漏写了 `className()` (基类 view() 里直接调它), 两次都是同一类错误:
 *   **给 Modal 子类加字段前, 先确认那个名字不在上面这份保留名单里。**
 *   ⇒ 正文字段现在叫 `reviewText`, 加载标志叫 `loadingReview`, 取数方法叫 `loadReview()`。
 *
 * ⚠️ 这类错误**不报错也不白屏**, 只表现为「点了没反应」, 从控制台看还像应用崩溃,
 *   所以排查时不要怀疑数据/接口, 先怀疑「有没有撞 Modal 的 API 名」。
 */

export default class LessonReviewModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.lesson = vnode.attrs.lesson || {};
    this.descriptor = vnode.attrs.descriptor || null;
    // 学员端只读 —— 由**调用方**传入 (它自己知道自己是学员), 但下面的 canEdit() 还会再收一次窄
    this.requestedReadonly = !!vnode.attrs.readonly;
    this.onChange = vnode.attrs.onChange || null;
    this.loadingReview = true;
    this.saving = false;
    this.error = '';
    this.uploading = false;

    // ⚠️ 星级初值 = 5 (辉哥追加规则)。草稿/已发布的既有分数会被 load() 覆盖。
    this.rating = 5;
    this.reviewText = '';
    this.media = [];
    this.serverCanWrite = false;
    this.loadReview();
  }
  onremove(vnode) {
    // 灯箱那层的 inline z-index 随节点自己消失, modal 侧不需要还原任何东西。
    // (第一版这里调 restoreModalLayer(), 那时方案是「抬 modal」—— 已废弃, 见 openMedia 的注释。)
    super.onremove(vnode);
  }

  /**
   * ⛔⛔ **每个具体 Modal 都必须自己定义 className()** —— Flarum 的 Modal 基类**不提供**它。
   *   基类的 `view()` 里直接调 `this.className()` 去拼 `<div class="Modal … {className()}">`,
   *   基类没定义 ⇒ 少定义就是 `TypeError: this.className is not a function`,
   *   **整个 modal 连标题栏都渲染不出来** (本轮实测踩到: 空白 modal + 后续 onbeforeupdate 连锁报错)。
   *   core 里每一个 Modal (RenameDiscussionModal / UserSelectionModal / …) 都有这一行,
   *   不是可选项。⚠️ 它同时是前端抓「本 modal 那一层」的稳定选择器 (`.Modal.LessonReviewModal`),
   *   比「第几个 ModalManager」可靠 —— 层号会随嵌套弹层变化。
   */
  className() {
    return 'LessonReviewModal';
  }
  title() {
    if (this.requestedReadonly) return '课评';
    return this.isPublished() ? '修改课评' : '写课评';
  }

  /* ── 数据 ───────────────────────────────────────────────────────────── */

  async loadReview() {
    try {
      const id = this.lesson.id;
      const res = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + `/ziiven/scheduler/lessons/${id}/review`
      });
      const data = res && res.data || null;

      // can_write 由**服务端**给 (GetLessonReviewController)。前端不自己推断权限。
      this.serverCanWrite = !!(res && res.can_write);
      if (data) {
        this.rating = data.rating || 5;
        this.reviewText = data.content || '';
        this.media = Array.isArray(data.media) ? data.media : [];
        this.descriptor = {
          status: data.status,
          rating: data.rating
        };
      } else {
        // 服务端说「没有可见的课评」: 草稿对学员 ≡ 不存在 (规则 6), 已取消的课评同样隐藏 (规则 2)
        this.rating = 5;
        this.reviewText = '';
        this.media = [];
        this.descriptor = this.requestedReadonly ? null : this.descriptor;
      }
    } catch (e) {
      this.error = '课评加载失败: ' + (e && e.message ? e.message : e);
    } finally {
      this.loadingReview = false;
      this.redraw();
    }
  }

  /* ── 状态判定 ───────────────────────────────────────────────────────── */

  isPublished() {
    return !!this.descriptor && this.descriptor.status === 'published';
  }
  isDraft() {
    return !!this.descriptor && this.descriptor.status === 'draft';
  }

  /**
   * 能不能编辑。
   *
   * ⚠️ 两个条件都要: 调用方说「我要只读」(学员) **且** 服务端说「你不能写」。
   *   只判前者的话, 某个传错标志的调用点会把只读 modal 变成可编辑;
   *   只判后者的话, 学员恰好没有 can_write 时行为相同 —— 两个都留是防回归。
   */
  canEdit() {
    return !this.requestedReadonly && this.serverCanWrite;
  }

  /** 已发布态的 modal **不提供**保存草稿 (规则 8/19) */
  canSaveDraft() {
    return this.canEdit() && !this.isPublished();
  }
  redraw() {
    if (typeof m !== 'undefined' && m && typeof m.redraw === 'function') m.redraw();
  }

  /* ── 保存 ───────────────────────────────────────────────────────────── */

  async save(publish) {
    if (this.saving) return;
    this.saving = true;
    this.error = '';
    this.redraw();
    try {
      const body = {
        data: {
          attributes: {
            rating: this.rating,
            content: this.reviewText,
            media: this.media,
            publish: !!publish
          }
        }
      };
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + `/ziiven/scheduler/lessons/${this.lesson.id}/review`,
        body,
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      this.descriptor = {
        status: publish ? 'published' : 'draft',
        rating: this.rating
      };
      if (this.onChange) this.onChange(this.descriptor);
      this.hide();
    } catch (e) {
      // R1 在这里会收到 422 —— 前端只负责把服务端的话显示出来, 不自己解释规则
      this.error = describeError(e);
    } finally {
      this.saving = false;
      this.redraw();
    }
  }

  /* ── 媒体 ───────────────────────────────────────────────────────────── */

  async onPickMedia(e) {
    const files = Array.prototype.slice.call(e.target && e.target.files || []);
    e.target.value = ''; // 允许连续选同一个文件
    if (files.length === 0) return;

    // 上限 6 是**混装**上限 (规则 15): 图片视频共用一个计数, 不分开算
    const room = 6 - this.media.length;
    if (room <= 0) {
      this.error = '图片和视频合计最多 6 个';
      return;
    }
    const queue = files.slice(0, room);
    this.uploading = true;
    this.error = '';
    this.redraw();
    try {
      // Phase D2-A: **先**按动态上限筛一遍, 再开始传。
      //   为什么要前端筛而不是靠后端报错 —— 后端那两句 (nginx 413 / PHP upload_max_filesize)
      //   用户完全看不懂, 而且传 500MB 到一半才被拒, 白等很久。
      //   上限本身从后端动态取 (见 utils/uploadLimits.js), 换档 / 改配置都会跟着变。
      const limit = await resolveUploadLimit();
      if (limit.bytes !== null) {
        const tooBig = queue.filter(f => f.size > limit.bytes);
        if (tooBig.length) {
          const names = tooBig.map(f => f.name || '未命名').join('、');
          this.error = overLimitMessage(tooBig[0].size, limit) + '  (超大文件: ' + names + ')';
          this.uploading = false;
          this.redraw();
          return;
        }
        // 取不到真实上限时也**不静默**: 提示一句, 让用户知道这个数是保守估计
        if (limit.degraded) {
          this.error = overLimitMessage(0, limit);
        }
      }
      let uploaded = 0;
      for (const file of queue) {
        const url = await uploadMedia(file);
        this.media = this.media.concat([{
          url,
          type: mediaTypeFor(file)
        }]);
        uploaded++;
        this.redraw();
      }
      if (files.length > room) {
        this.error = `最多 6 个，已忽略多出的 ${files.length - room} 个`;
      } else if (uploaded === queue.length && !limit.degraded) {
        this.error = '';
      }
    } catch (err) {
      this.error = '上传失败: ' + (err && err.message ? err.message : err);
    } finally {
      this.uploading = false;
      this.redraw();
    }
  }
  removeMedia(index) {
    this.media = this.media.filter((_, i) => i !== index);
    this.redraw();
  }
  openMedia(index) {
    // 学员端也点得开 (规则 20: 媒体可点开大图/播放), 所以这个方法**不**判 canEdit。
    // ⛔ z-index 由 openMediaLightbox 自己处理 (提**灯箱**那一层), modal 侧**不动** ——
    //   实测「抬 modal」会把灯箱压在下面, 方向反了, 见 utils/LessonReviewMedia 顶部的实测记录。
    openMediaLightbox(this.media, index);
  }

  /* ── 上下文条数据 ───────────────────────────────────────────────────── */

  studentName() {
    const s = this.lesson.student;
    if (s) return s.display_name || s.username || '';
    if (this.lesson.customer_name) return this.lesson.customer_name;
    return this.lesson.student_id ? '学员 #' + this.lesson.student_id : '—';
  }
  courseLabel() {
    const parts = [];
    if (this.lesson.lesson_type_id) {
      parts.push(LessonOptions.labelFor('type', this.lesson.lesson_type_id));
    }
    const cats = this.lesson.lesson_category_id ? [this.lesson.lesson_category_id] : this.lesson.student_card && this.lesson.student_card.template && Array.isArray(this.lesson.student_card.template.lesson_category_ids) ? this.lesson.student_card.template.lesson_category_ids : [];
    cats.forEach(c => {
      const label = LessonOptions.labelFor('category', c);
      if (label) parts.push(label);
    });
    return parts.join(' · ');
  }

  /** 「2026-10-18 14:00–15:00」—— 墙上时间按浏览器本地时区解析 (与全站课表同口径) */
  timeLabel() {
    const s = this.lesson.start_at;
    const e = this.lesson.end_at;
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d.getTime())) return String(s).slice(0, 16);
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const from = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    if (!e) return `${date} ${from}`;
    const d2 = new Date(e);
    if (isNaN(d2.getTime())) return `${date} ${from}`;
    return `${date} ${from}–${pad(d2.getHours())}:${pad(d2.getMinutes())}`;
  }
  placeLabel() {
    return [this.lesson.school_name, this.lesson.room_name].filter(Boolean).join(' ');
  }
  statusLabel() {
    const map = {
      scheduled: '已预约',
      confirmed: '已确认',
      'checked-in': '已签到',
      completed: '已消课',
      cancelled: '已取消'
    };
    return map[this.lesson.status] || this.lesson.status || '';
  }
  statusClass() {
    return this.lesson.status || 'scheduled';
  }

  /* ── 渲染 ───────────────────────────────────────────────────────────── */

  contentCounter() {
    return `${(this.reviewText || '').length}/300`;
  }
  counterClass() {
    const n = (this.reviewText || '').length;
    // 规则 16: ≥280 转 @warning, 300/300 转 @danger
    if (n >= 300) return 'danger';
    if (n >= 280) return 'warning';
    return '';
  }
  avatar() {
    const s = this.lesson.student;
    if (s && s.id) {
      const u = app.store.getById('users', s.id);
      if (u) return m("span", {
        className: "ZS-review-ctx-avatar"
      }, m(Avatar, {
        user: u
      }));
      return m("span", {
        className: "ZS-review-ctx-avatar is-letter"
      }, (this.studentName() || '学').charAt(0));
    }
    return m("span", {
      className: "ZS-review-ctx-avatar is-letter"
    }, (this.studentName() || '学').charAt(0));
  }

  /**
   * 评分行: 恒 5 颗 —— n 颗**橙色实心** + (5−n) 颗**灰色空心**。
   *
   * 🔴 v0.18.1 (辉哥 10/18 09:22 拍板 A 方案「线性空心灰星」)
   *
   * ## ⛔⛔ 第一版为什么是**坏的** (这个 bug 静默了很久)
   *   第一版空心星写的是 `fa-star-o` —— 那是 **FontAwesome 4** 的老类, 本仓跑的是
   *   **Font Awesome Free 7.3.1**。实测两端下发的 `forum.css`:
   *     独立 `.fa-star-o` 命中 **0** (唯独那 2 处是 `.fa-star-of-life` /
   *     `.fa-star-of-david` 的**子串**, 不是这个类)
   *   ⇒ 4 星及以下时, 第 5 颗是个**不存在的类**: 既不渲染灰星也不报错, 顶多一个豆腐块,
   *     那个内联灰 `#4b5563` 也白给了, 因为压根没有字形。
   *   **为什么一直没被发现**: rating 默认 5 星 ⇒ `i <= n` 恒真 ⇒ 那个分支**永远走不到**。
   *   只有教练主动点出 4 星或更低才会暴露。
   *
   * ## 空心为什么选 `far` 而不是 `fa-regular` (两者都实测存在)
   *   两者在 CSS 里落在**同一个** `:is(.fas,.far,.fab,.fa-solid,.fa-regular,…)` 选择器列表,
   *   解析结果完全一致 ⇒ 挑短的。`far` 同时与本仓既有写法一致
   *   (`StudentLessonsModal.js` 早就用 `fas fa-star`), 不引入第二种风格别名。
   *   `.far` 规则实测为 `--fa-family: var(--fa-family-classic); --fa-style: 400;`
   *   ⇒ 走 `fa-regular-400.woff2`, 里面确有星形字形。
   *
   * ## 灰色为什么走 LESS class 而不是内联
   *   两个原因, 少一个都不该这么写:
   *     1. **对比度**: 内联的 `#4b5563` vs modal 深色底 `@body-bg #15181d` 只有 **2.35:1**,
   *        而 WCAG 2.1 SC 1.4.11 非文字图形要求 **≥3:1** ⇒ 那个灰本身就不合格。
   *        改用 `@text-muted #9ca3af` = **7.01:1**。
   *     2. **本仓有 LESS 特异性翻车前科** (见 forum.less 里 StudentLessonsModal 那段注释),
   *        内联 style 赢不了样式表里的覆盖, 而 class 可以用嵌套拿到明确的特异性。
   *   ⇒ `is-empty` 类在 LESS 里定义为 `.ZS-review-stars i.is-empty` (0,2,1),
   *     刻意强过同块里的 `.ZS-review-stars i` (0,1,1), 否则颜色不生效。
   *
   * ## 实心分支逐字未改
   *   `i <= n` 分支仍是 `'fa-star'` —— 实心星的表现一个字都没动。
   */
  starsRow(readonly) {
    const n = this.rating || 0;
    return m("span", {
      className: "ZS-review-stars"
    }, [1, 2, 3, 4, 5].map(i => m("i", {
      key: i,
      className: 'fa ' + (i <= n ? 'fa-star' : 'far fa-star is-empty'),
      onclick: readonly ? null : () => {
        this.rating = i;
        this.redraw();
      }
    })));
  }

  /**
   * ⛔⛔ 回调参数**绝不能叫 `m`** —— JSX 编译后的 hyperscript 就是全局的 `m()`。
   *   本组件第一版写成 `this.media.map((m, i) => m("div", …))`, 这里的 `m` 把 JSX 的 `m` 遮住了,
   *   运行时报 **`TypeError: m is not a function`**, 整个 modal 白屏, 而**编译期零告警**。
   *   ⇒ 凡是含 JSX 的 map/forEach, 回调参数避开 m / h / vnode 这类 JSX 运行时保留名。
   */
  mediaGrid(readonly) {
    return m("div", {
      className: "ZS-review-grid"
    }, this.media.map((item, i) => m("div", {
      className: "ZS-review-cell",
      key: item.url + i,
      onclick: () => this.openMedia(i)
    }, item.type === 'video' ? m("video", {
      className: "ZS-review-media",
      src: item.url,
      muted: true,
      preload: "metadata"
    }) : m("img", {
      className: "ZS-review-media",
      src: item.url,
      alt: "",
      loading: "lazy"
    }), m("span", {
      className: "ZS-review-cell-badge"
    }, m("i", {
      className: 'fas ' + (item.type === 'video' ? 'fa-play' : 'fa-image')
    })), this.canEdit() && !readonly && m("button", {
      className: "ZS-review-cell-del",
      title: "\u79FB\u9664",
      onclick: e => {
        e.stopPropagation();
        this.removeMedia(i);
      }
    }, m("i", {
      className: "fas fa-xmark"
    })))), this.canEdit() && this.media.length < 6 && m("label", {
      className: 'ZS-review-cell add' + (this.uploading ? ' busy' : '')
    }, m("input", {
      type: "file",
      accept: "image/*,video/*",
      multiple: true,
      onchange: e => this.onPickMedia(e),
      style: "display:none;"
    }), m("i", {
      className: 'fas ' + (this.uploading ? 'fa-circle-notch fa-spin' : 'fa-plus')
    }), m("span", null, this.uploading ? '上传中' : '添加')));
  }

  /**
   * ⛔ **必须叫 `content()`, 不能叫 `view()`** (Flarum 的 Modal 走 content 渲染)。
   *   写成 `view()` 会**覆盖**基类的 view(), 于是 Modal 自带的外壳
   *   (`.Modal-content` / `.Modal-body` / `.Modal-footer` 的包裹与标题栏) 整个不渲染 ——
   *   肉眼看着"能用", 但 ModalManager 的层级结构断了, 样式与灯箱层级都没法按 Flarum 的规则算。
   *   实测症状: `document.querySelectorAll('.Modal')` 返回 **0 个**。
   *   本仓其它 modal (SalesFollowupCreateModal / CardDetailModal …) 一律是 content()。
   */
  content() {
    const readonly = !this.canEdit();
    return m("div", {
      className: "ZS-review-modal"
    }, m("div", {
      className: "ZS-review-ctx"
    }, this.avatar(), m("div", {
      className: "ZS-review-ctx-main"
    }, m("div", {
      className: "ZS-review-ctx-name"
    }, this.studentName()), this.courseLabel() && m("div", {
      className: "ZS-review-ctx-course"
    }, this.courseLabel())), m("span", {
      className: 'ZS-status-tag ' + this.statusClass()
    }, this.statusLabel())), m("div", {
      className: "ZS-review-ctx-meta"
    }, m("span", null, m("i", {
      className: "fas fa-calendar-day"
    }), " ", this.timeLabel()), this.placeLabel() && m("span", null, m("i", {
      className: "fas fa-location-dot"
    }), " ", this.placeLabel())), this.loadingReview ? m("div", {
      className: "ZS-review-loading"
    }, "\u52A0\u8F7D\u4E2D\u2026") : m("div", {
      className: "ZS-review-body"
    }, this.isDraft() && m("div", {
      className: "ZS-review-banner draft"
    }, m("i", {
      className: "fas fa-circle-info"
    }), "\u8349\u7A3F\u53EA\u6709\u4F60\u80FD\u770B\u5230\uFF0C\u53D1\u5E03\u540E\u5B66\u5458\u624D\u80FD\u770B\u5230"), this.isPublished() && m("div", {
      className: "ZS-review-banner published"
    }, m("i", {
      className: "fas fa-circle-check"
    }), readonly ? '已发布课评，学员可见' : '已发布，学员可见（不支持撤回发布，改动会直接覆盖）'), m("div", {
      className: "ZS-review-field"
    }, m("label", {
      className: "ZS-review-label"
    }, "\u8BC4\u5206", readonly && m("em", {
      className: "ZS-review-score"
    }, this.rating, " \u5206")), readonly ? m("span", {
      className: "ZS-review-stars readonly"
    }, this.starsRow(true)) : this.starsRow(false)), m("div", {
      className: "ZS-review-field"
    }, m("label", {
      className: "ZS-review-label"
    }, "\u6587\u5B57", !readonly && m("em", {
      className: 'ZS-review-counter ' + this.counterClass()
    }, this.contentCounter())), readonly ? m("div", {
      className: "ZS-review-text readonly"
    }, this.reviewText || m("span", {
      className: "ZS-review-empty"
    }, "\u6559\u7EC3\u6CA1\u6709\u5199\u6587\u5B57\u8BC4\u4EF7")) : m("textarea", {
      className: "ZS-review-text",
      value: this.reviewText,
      maxlength: 300,
      rows: 4,
      placeholder: "\u8FD9\u8282\u8BFE\u8BB2\u4E86\u4EC0\u4E48\uFF1F\u5B66\u5458\u7684\u4E0A\u8BFE\u611F\u53D7\u2026",
      oninput: e => {
        this.reviewText = e.target.value;
        this.redraw();
      }
    })), m("div", {
      className: "ZS-review-field"
    }, m("label", {
      className: "ZS-review-label"
    }, "\u56FE\u7247 / \u89C6\u9891", readonly ? this.media.length > 0 && m("em", {
      className: "ZS-review-counter"
    }, this.media.length, " \u4E2A") : m("em", {
      className: "ZS-review-counter"
    }, this.media.length, "/6")), this.media.length === 0 && readonly && m("div", {
      className: "ZS-review-empty-block"
    }, "\u6682\u65E0\u8BFE\u8BC4"), (!readonly || this.media.length > 0) && this.mediaGrid(readonly)), this.error && m("div", {
      className: "ZS-review-error"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", this.error)), m("div", {
      className: "Modal-footer ZS-review-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, m("i", {
      className: "fas fa-xmark"
    }), " ", this.requestedReadonly ? '关闭' : '取消'), this.canSaveDraft() && m("button", {
      className: "Button",
      disabled: this.saving || this.loadingReview,
      onclick: () => this.save(false)
    }, m("i", {
      className: "fas fa-floppy-disk"
    }), " \u4FDD\u5B58\u8349\u7A3F"), this.canEdit() && m("button", {
      className: "Button Button--primary",
      disabled: this.saving || this.loadingReview,
      onclick: () => this.save(true)
    }, m("i", {
      className: 'fas ' + (this.isPublished() ? 'fa-check' : 'fa-paper-plane')
    }), this.saving ? '保存中…' : this.isPublished() ? '更新课评' : '发布课评')));
  }
}
function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

/** 把 Flarum 的异常翻译成人话。⛔ 服务端 422 的原文优先展示 —— 那是规则的唯一事实源。 */
function describeError(e) {
  if (!e) return '保存失败';
  const payload = e.json || (typeof e.responseText === 'string' ? null : null);
  const body = payload && (payload.message || payload.error) || null;
  if (typeof body === 'string') return body;
  return '保存失败: ' + (e.message || e);
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/LessonReviewModal', LessonReviewModal);